import React from 'react';
import { GetServerSideProps, NextPageWithLayout, Redirect } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { dehydrate, DehydratedState, QueryClient, useHydrate } from '@tanstack/react-query';
import { ContentStackPageNames } from '@enums/contentStack';
import CMSPage from '@components/organisms/content-stack/cms-page';
import { IContentTypeEntries } from '@constants/metas/default';
import ContentStackService from '@services/content-stack-service';
import extractFromContext from '@helpers/utils/extract-from-context';
import DefaultLayout from '@layouts/default-layout';
import StickyNavEntryPointWrapper from '@components/molecules/sticky-nav-entry-point/sticky-nav-entry-point-wrapper';
import { isRequest404Error, isRequestError } from '@helpers/errors';
import logger from '@helpers/utils/logger/client';
import { checkShouldShowBanner } from '@helpers/utils/general';
import { getAppInitialProps } from '@helpers/initial-props';
import getServerFeatureFlag from '@hooks/bucket-test/use-ssr-feature-flag';
import { getOrCreateLDCookiesSsr } from '@components/bucketTest/bucket.utils';
import { prefetchAndSetStickyNavData } from '@helpers/utils/sticky-nav/sticky-nav-prefetch';

export const pagePathToPageNameMap = new Map([
  ['app-ds-ed', ContentStackPageNames.DS_EXPRESS_DELIVERY_APP_PAGE],
  ['daily-deals', ContentStackPageNames.DAILY_DEALS_PAGE],
  ['my-community', ContentStackPageNames.DAILY_DEALS_PAGE],
  ['top-seller', ContentStackPageNames.TOP_SELLERS_PAGE],
  ['channels', ContentStackPageNames.CHANNEL_PAGE],
  ['description', ContentStackPageNames.LANDING_WITH_DESCRIPTION_AND_PRODUCT_PAGE],
  ['raas', ContentStackPageNames.RAAS_PAGE],
  ['web-ds-ed', ContentStackPageNames.DS_EXPRESS_DELIVERY_WEB_PAGE],
  ['custom', ContentStackPageNames.CUSTOM_PAGE],
  ['homemade-by-vc', ContentStackPageNames.HOME_MADE_BY_VC_PAGE],
  ['l', ContentStackPageNames.CUSTOM_PAGE],
  ['talking-fashion', ContentStackPageNames.TALKING_FASHION_PAGE_WEB_PAGE],
]);

type CMSRootPageProps = {
  contentTypeEntries: IContentTypeEntries;
  isLivePreview?: boolean;
  isVestiaireReferrer?: boolean;
  dehydratedState?: DehydratedState;
};

const CMSRootPage: NextPageWithLayout<CMSRootPageProps> = (props) => {
  const { contentTypeEntries, isLivePreview, isVestiaireReferrer, dehydratedState } = props;
  const shouldShowBanner = checkShouldShowBanner(contentTypeEntries);

  useHydrate(dehydratedState);

  return (
    <DefaultLayout shouldShowBanner={shouldShowBanner}>
      <StickyNavEntryPointWrapper isVestiaireReferrer={isVestiaireReferrer} />
      <CMSPage {...{ contentTypeEntries, isLivePreview }} />
    </DefaultLayout>
  );
};

CMSRootPage.Layout = [];
CMSRootPage.PageType = 'CMSPage';

export const getServerSideProps: GetServerSideProps<CMSRootPageProps | Redirect> = async (context) => {
  const { preferences, locale, userId } = extractFromContext(context);
  const [pagePath, ...rest] = context?.params?.cmsSlug as string[];
  const pathName = pagePathToPageNameMap.get(pagePath);

  // If requested path isn't registered as a CMS path, redirect to 404
  if (!pathName) {
    return {
      notFound: true,
    };
  }

  const isLivePreview = context?.query?.live_preview === 'true';
  const { serverAnonymousId, randomRangeId } = getOrCreateLDCookiesSsr(context);
  const { getFeatureFlag } = getServerFeatureFlag(context, serverAnonymousId, randomRangeId);
  const queryClient = new QueryClient();

  try {
    const initialProps = await getAppInitialProps(context, queryClient);

    if (isLivePreview) {
      const translations = await serverSideTranslations(locale, [
        'default-layout',
        'vc-authentication',
        'a11y',
        'common',
      ]);

      return {
        props: {
          ...initialProps,
          ...translations,
          contentTypeEntries: {} as IContentTypeEntries,
          isLivePreview: true,
          dehydratedState: dehydrate(queryClient),
        },
      };
    }

    const [translations, contentTypeEntries] = await Promise.all([
      serverSideTranslations(locale, ['default-layout', 'vc-authentication', 'a11y', 'common']),
      ContentStackService.getDataForCMSPage(
        pathName,
        {
          ...preferences,
          userId,
          userAgent: context?.req?.headers?.['user-agent'],
        },
        { pageSlug: rest.at(-1) },
      ),
    ]);

    if (!contentTypeEntries) {
      return {
        notFound: true,
      };
    }

    const stickyNavParams = await prefetchAndSetStickyNavData(context, initialProps, queryClient, getFeatureFlag);

    return {
      props: {
        ...initialProps,
        ...translations,
        contentTypeEntries,
        isVestiaireReferrer: stickyNavParams.isVestiaireReferrer,
        dehydratedState: dehydrate(queryClient),
      },
    };
  } catch (err) {
    if (isRequestError(err)) {
      if (!isRequest404Error(err)) {
        logger.error(
          err,
          'There was an api request error on the CMS Page - getServerSideProps - [[...cmsSlug]].page.tsx',
        );
      }
      return {
        notFound: true,
      };
    }
    logger.error(err, 'The server failed to render the CMS Page - getServerSideProps - [[...cmsSlug]].page.tsx');
    throw err;
  }
};

export default CMSRootPage;
